import { useEffect } from "react";
import axios from "axios";
import { mapMoment, getMomentColor, moments } from "../constants/moments.js";
import { useMenu } from "../context/MenuContext.js";
import isEqual from "fast-deep-equal";
import { logDev } from "../utils/logger.js"; 

// ------------------------------------------------------------
// Normalisation d'une liste d'items pour un moment (Matin/Midi/Soir)
// - garantit un item vide si la catégorie est vide
// - applique couleur + flags cohérents
// ------------------------------------------------------------

const normalizeItems = (items, momentKey) => {
const momentColor = getMomentColor(momentKey);


// ❌ Ne crée plus d'item vide automatiquement
if (!items || items.length === 0) {
    return [];
  }
  
// affiche ls items de la base
  return items 
    .filter(it => it && (it._id || it.name?.trim() || it.quantity)) // 👈 élimine les items vides 
    .map(it => ({ 
      ...it, 
      quantity: it.quantity ?? 0, 
      saved: !!it.saved, 
      moment: momentKey, 
      color: it.color || momentColor,
     }));
};

// ------------------------------------------------------------
// Hook : useFetchMenuJournalier
// Objectif :
// 1. Charger TOUS les menus du groupe via GET /menusjournalier/:groupeId
// 2. Sélectionner celui correspondant à dateIso
// 3. Normaliser repas → catégories → items
// 4. Stocker dans MenuContext (itemsMenus[dateIso])
// ------------------------------------------------------------
export const useFetchMenuJournalier = ({
  jour,
  groupeId,
  fetchedDates,
  setItems,
  setItemsMenus
}) => {

  const menuContext = useMenu();
  const { itemsMenus } = menuContext;

  useEffect(() => {
    if (!jour || !groupeId) return;
    logDev("🟥 useFetchMenuJournalier MONTÉ", { jour, groupeId });

    const dateIso = jour.toISOString().split("T")[0];   // YYYY-MM-DD
    
    logDev("[useFetchMenuJournalier] date normalisée", { dateIso, groupeId });
    logDev("🟥 fetchedDates avant test", Array.from(fetchedDates.current));

    if (fetchedDates.current.has(dateIso)) {
      return;
    } // évite refetch

    logDev("🟥 FETCH AUTORISÉ → on appelle l’API");
    
    const fetchMenu = async () => {
      try {
        logDev("[useFetchMenuJournalier] Appel API", {
          dateIso,
          groupeId
        });

        // 🔥 Récupère tous les menus du groupe
        const res = await axios.get(
         `${process.env.REACT_APP_API}/menusjournalier/${groupeId}`
        );

        // 🔍 Sélection du menu du jour
        const menu = res.data.find(m => 
          m.date.startsWith(dateIso)
        );

        if (!menu) {
          logDev(`[useFetchMenuJournalier] Aucun menu pour ${dateIso}`);
          fetchedDates.current.add(dateIso);
        return;
        }

      const { repas = [] } = menu;

      const normalizedRepas = repas.map(r => ({
        _id: r._id, // 🔥 indispensable
        moment: mapMoment(r.moment),
        categories: r.categories.map(cat => ({
        _id: cat._id, // 🔥 utile pour POST /items
        categorie: cat.categorie,
        items: normalizeItems(cat.items, mapMoment(r.moment))
      }))
    }));


    // 🔥 Mise à jour du MenuContext
    setItemsMenus(prev => ({
      ...prev,
      [dateIso]: normalizedRepas
    }));

    // 🔥 Mise à jour du state local
    setItems(normalizedRepas);

    fetchedDates.current.add(dateIso);

    } catch (err) {
      if (err.response?.status === 404) {
        // Aucun menu → structure vide
        logDev(`[useFetchMenuJournalier] Aucun menu trouvé pour ${dateIso} (groupe ${groupeId}), initialisation vide.`);
        fetchedDates.current.add(dateIso);
          
        logDev("[useFetchMenuJournalier] date marquée comme fetchée", { dateIso });

        setItems([]);
        setItemsMenus(prev => ({ ...prev, [dateIso]: [] }));
   
        } else {
          logDev("[useFetchMenuJournalier] Erreur serveur/réseau", { dateIso, groupeId, err });
          // ❌ Cas anormal : vrai problème serveur ou réseau
          console.error("Erreur chargement menu :", err);
        }
      }
    };

    fetchMenu();
  }, [jour, groupeId]);
};
